import { generateId } from './id-helpers';

function generateUniqueQuestionRef(baseRef: string, existingRefs: Set<string>): string {
  const candidate = `${baseRef}-copy`;
  if (!existingRefs.has(candidate)) return candidate;

  let counter = 2;
  while (existingRefs.has(`${baseRef}-copy-${counter}`)) {
    counter++;
  }
  return `${baseRef}-copy-${counter}`;
}

function remapCondition(
  condition: Condition | undefined,
  refMap: Map<string, string>,
): Condition | undefined {
  if (!condition) return undefined;

  if ('operator' in condition && 'conditions' in condition) {
    const logical = condition as LogicalCondition;
    return {
      operator: logical.operator,
      conditions: logical.conditions.map(c => remapCondition(c, refMap)!).filter(Boolean),
    };
  }

  const field = (condition as FieldCondition).field;
  if (field && refMap.has(field)) {
    return { ...condition, field: refMap.get(field)! } as FieldCondition;
  }

  return condition;
}

function remapConditionalBehavior(
  behavior: ConditionalBehavior | undefined,
  refMap: Map<string, string>,
): ConditionalBehavior | undefined {
  if (!behavior) return undefined;

  const result: ConditionalBehavior = {};
  if (behavior.visible) result.visible = remapCondition(behavior.visible, refMap);
  if (behavior.required) result.required = remapCondition(behavior.required, refMap);
  if (behavior.showConditionalText)
    result.showConditionalText = remapCondition(behavior.showConditionalText, refMap);
  return result;
}

function cloneQuestion(
  question: PolicyFormQuestion,
  newRef: string,
  refMap: Map<string, string>,
): PolicyFormQuestion {
  const cloned: PolicyFormQuestion = JSON.parse(JSON.stringify(question));
  cloned.id = generateId('question');
  cloned.questionRef = newRef;

  cloned.conditional = remapConditionalBehavior(cloned.conditional, refMap);

  if (cloned.composite) {
    cloned.composite = {
      ...cloned.composite,
      questionRefs: cloned.composite.questionRefs.map(ref => refMap.get(ref) ?? ref),
      conditional: remapCondition(cloned.composite.conditional, refMap),
    };
  }

  if (cloned.options) {
    cloned.options = cloned.options.map(opt => ({
      ...opt,
      conditional: opt.conditional
        ? (remapConditionalBehavior(opt.conditional, refMap) as OptionConditionalBehavior)
        : undefined,
    }));
  }

  return cloned;
}

interface CloneSubsectionResult {
  subsection: PolicyFormSubSection;
  questions: Record<string, PolicyFormQuestion>;
}

export function deepCloneSubsection(
  source: PolicyFormSubSection,
  allQuestions: Record<string, PolicyFormQuestion>,
  existingRefs: Set<string>,
): CloneSubsectionResult {
  const refMap = new Map<string, string>();
  const collectedRefs: string[] = [];

  for (const group of source.questionGroups) {
    for (const ref of group.questions) {
      collectedRefs.push(ref);
    }
  }

  const workingRefs = new Set(existingRefs);
  for (const oldRef of collectedRefs) {
    const newRef = generateUniqueQuestionRef(oldRef, workingRefs);
    refMap.set(oldRef, newRef);
    workingRefs.add(newRef);
  }

  const clonedQuestions: Record<string, PolicyFormQuestion> = {};
  for (const [oldRef, newRef] of refMap) {
    const original = allQuestions[oldRef];
    if (original) {
      clonedQuestions[newRef] = cloneQuestion(original, newRef, refMap);
    }
  }

  const clonedSubsection: PolicyFormSubSection = {
    id: generateId('subsection'),
    title: `${source.title || 'Untitled'} (Copy)`,
    conditional: remapConditionalBehavior(source.conditional, refMap),
    questionGroups: source.questionGroups.map(group => ({
      ...JSON.parse(JSON.stringify(group)),
      id: generateId('group'),
      questions: group.questions.map(ref => refMap.get(ref) ?? ref),
      conditional: remapConditionalBehavior(group.conditional, refMap),
    })),
  };

  return { subsection: clonedSubsection, questions: clonedQuestions };
}

interface CloneSectionResult {
  section: PolicyFormSection;
  questions: Record<string, PolicyFormQuestion>;
}

export function deepCloneSection(
  source: PolicyFormSection,
  allQuestions: Record<string, PolicyFormQuestion>,
  existingRefs: Set<string>,
): CloneSectionResult {
  const allClonedQuestions: Record<string, PolicyFormQuestion> = {};
  const allRefMap = new Map<string, string>();
  const workingRefs = new Set(existingRefs);

  for (const sub of source.subsections) {
    for (const group of sub.questionGroups) {
      for (const ref of group.questions) {
        const newRef = generateUniqueQuestionRef(ref, workingRefs);
        allRefMap.set(ref, newRef);
        workingRefs.add(newRef);
      }
    }
  }

  for (const [oldRef, newRef] of allRefMap) {
    const original = allQuestions[oldRef];
    if (original) {
      allClonedQuestions[newRef] = cloneQuestion(original, newRef, allRefMap);
    }
  }

  const clonedSection: PolicyFormSection = {
    id: generateId('section'),
    title: `${source.title || 'Untitled'} (Copy)`,
    conditional: remapConditionalBehavior(source.conditional, allRefMap),
    subsections: source.subsections.map(sub => ({
      id: generateId('subsection'),
      title: sub.title,
      conditional: remapConditionalBehavior(sub.conditional, allRefMap),
      questionGroups: sub.questionGroups.map(group => ({
        ...JSON.parse(JSON.stringify(group)),
        id: generateId('group'),
        questions: group.questions.map(ref => allRefMap.get(ref) ?? ref),
        conditional: remapConditionalBehavior(group.conditional, allRefMap),
      })),
    })),
  };

  return { section: clonedSection, questions: allClonedQuestions };
}
